import React from "react";
import { Button, FormGroup, Input, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import Joi from "joi-browser";
import { cambiarPassword } from "services/userService";
import { toastBigError, toastInfo } from "components/utils/toasts";

// Cambio de contraseña del usuario logueado, desde el sidebar. Pide la contraseña
// actual porque el server la exige: el id del usuario sale del token, no de acá.
const CambiarPasswordModal = ({ isOpen, onClose }) => {
  const vacio = { actual: "", password: "", confirm: "" };
  const [data, setData] = React.useState(vacio);
  const [errors, setErrors] = React.useState({});
  const [grabando, setGrabando] = React.useState(false);

  const schema = {
    actual: Joi.string()
      .label("Contraseña actual")
      .required()
      .error((errors) => errors.map(() => ({ message: "Debe ingresar su contraseña actual" }))),
    password: Joi.string()
      .label("Nueva contraseña")
      .min(6)
      .required()
      .error((errors) =>
        errors.map((error) => {
          switch (error.type) {
            case "string.min":
              return { message: "La nueva contraseña debe tener al menos 6 caracteres" };
            default:
              return { message: "Debe ingresar la nueva contraseña" };
          }
        })
      ),
    confirm: Joi.string()
      .label("Confirmación")
      .required()
      .error((errors) => errors.map(() => ({ message: "Debe confirmar la nueva contraseña" }))),
  };

  // Al abrir se arranca siempre en blanco: nunca queda una contraseña tipeada
  // de una vez anterior.
  React.useEffect(() => {
    if (isOpen) {
      setData(vacio);
      setErrors({});
      setGrabando(false);
    }
  }, [isOpen]);

  const validateProperty = (input) => {
    const object = { [input.name]: input.value };
    const checkSchema = { [input.name]: schema[input.name] };
    const { error } = Joi.validate(object, checkSchema);
    return error ? error.details[0].message : null;
  };

  const validate = () => {
    const cacheErrors = {};
    const { error } = Joi.validate(data, schema, { abortEarly: false });
    if (error) {
      error.details.map((validationError) => (cacheErrors[validationError.path[0]] = validationError.message));
      toastBigError(error.details[0].message);
      return cacheErrors;
    }
    if (data.password !== data.confirm) {
      toastBigError("Las contraseñas no coinciden");
      return { confirm: "Las contraseñas no coinciden" };
    }
    if (data.actual === data.password) {
      toastBigError("La nueva contraseña debe ser distinta de la actual");
      return { password: "La nueva contraseña debe ser distinta de la actual" };
    }
    return null;
  };

  const handleChange = (event) => {
    const input = event.currentTarget;
    const cacheErrors = { ...errors };
    const cacheData = { ...data };
    const errorMessage = validateProperty(input);
    if (errorMessage) cacheErrors[input.name] = errorMessage;
    else delete cacheErrors[input.name];
    cacheData[input.name] = input.value;
    setData(cacheData);
    setErrors(cacheErrors);
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") handleSubmit(event);
  };

  const handleSubmit = async (event) => {
    if (event) event.preventDefault();
    const checkErrors = validate();
    if (checkErrors) {
      setErrors(checkErrors);
      return;
    }
    setGrabando(true);
    // Los errores del server (contraseña actual incorrecta, etc.) ya los toastea
    // el interceptor de httpService: acá sólo dejamos el modal abierto.
    const respuesta = await cambiarPassword(data);
    setGrabando(false);
    if (respuesta && respuesta.success) {
      toastInfo("Se cambió su contraseña correctamente");
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered>
      <ModalHeader toggle={onClose}>Cambiar Contraseña</ModalHeader>
      <ModalBody>
        <FormGroup className={`has-label ` + (errors["actual"] ? "has-danger" : "")}>
          <label>Contraseña actual</label>
          <Input
            id="actual"
            name="actual"
            type="password"
            autoComplete="current-password"
            value={data.actual}
            onChange={handleChange}
            onKeyPress={handleKeyPress}
          />
        </FormGroup>
        <FormGroup className={`has-label ` + (errors["password"] ? "has-danger" : "")}>
          <label>Nueva contraseña</label>
          <Input
            id="password"
            name="password"
            type="password"
            autoComplete="new-password"
            value={data.password}
            onChange={handleChange}
            onKeyPress={handleKeyPress}
          />
        </FormGroup>
        <FormGroup className={`has-label ` + (errors["confirm"] ? "has-danger" : "")}>
          <label>Confirmar nueva contraseña</label>
          <Input
            id="confirm"
            name="confirm"
            type="password"
            autoComplete="new-password"
            value={data.confirm}
            onChange={handleChange}
            onKeyPress={handleKeyPress}
          />
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button className="btn-cancelar btn-sm my-0" onClick={onClose} disabled={grabando}>
          Cancelar
        </Button>
        <Button className="btn-arhock btn-sm my-0" onClick={handleSubmit} disabled={grabando}>
          {grabando ? "Cambiando..." : "Aceptar"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default CambiarPasswordModal;
